const PRODUCT_STORAGE_KEY = "lexiProduct";

const notifyProductChange = (productName) => {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(
      new CustomEvent("lexi:product-updated", { detail: { productName } }),
    );
  } catch {
    // noop
  }
};

export const decodeJwtPayload = (token) => {
  if (!token || typeof token !== "string") return null;

  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;

    // Handle base64url tokens
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
    return JSON.parse(atob(padded));
  } catch (error) {
    console.warn("Unable to decode auth token payload", error);
    return null;
  }
};

export const persistProductName = (productName) => {
  if (!productName) return null;

  sessionStorage.setItem(PRODUCT_STORAGE_KEY, productName);
  localStorage.setItem(PRODUCT_STORAGE_KEY, productName);
  notifyProductChange(productName);

  return productName;
};

/**
 * Get the current product name from the JWT token.
 *
 * Priority order:
 * 1. Active/trialing subscription (Lexi Pro, etc.)
 * 2. One-time payments (7-Day Pass, etc.)
 * 3. Default to "Lexi Social" (free tier)
 *
 * @returns {string|null} Product name or null if no token
 */
export const getStoredProductName = () => {
  // In dev, treat user as Lexi Pro so all features are testable locally
  if (import.meta.env.VITE_APP_WORKSPACE === "dev") {
    return "Lexi Pro";
  }

  // Always read from JWT token (don't use cached value)
  const token =
    sessionStorage.getItem("authToken") || localStorage.getItem("authToken");

  // If no token, return null (user is logged out)
  if (!token) {
    return null;
  }

  const payload = decodeJwtPayload(token);

  // Priority 1: Check for active subscriptions
  const subscription = payload?.subscription;

  if (subscription && Array.isArray(subscription) && subscription.length > 0) {
    // Get product name from first active/trialing subscription
    const activeSubscription =
      subscription.find(
        (sub) => sub.status === "active" || sub.status === "trialing",
      ) || subscription[0];

    const productName = activeSubscription?.items?.[0]?.price?.product?.name;

    if (productName) {
      return productName;
    }
  }

  // Priority 2: Check for one-time payments (e.g., 7-Day Pass)
  const oneTimePayments = payload?.oneTimePayments;

  if (
    oneTimePayments &&
    Array.isArray(oneTimePayments) &&
    oneTimePayments.length > 0
  ) {
    // Get the most recent one-time payment (defensive copy to avoid mutating original)
    const sortedPayments = [...oneTimePayments].sort(
      (a, b) => b.created - a.created,
    );
    const mostRecent = sortedPayments[0];

    if (mostRecent?.productName) {
      return mostRecent.productName;
    }
  }

  // Priority 3: Default to "Lexi Social" (free tier)
  return "Lexi Social";
};
